/**
 * A ranked distribution: a label, a proportional bar and a count, per row.
 *
 * Shared by the technology-mix and rejection-reason cards, which are the same
 * shape — a category and how many records fall in it. The bar is a single
 * neutral accent, never green: this is a spread across categories, not a
 * measure of "how good", and §4.2 reserves green for data values that ARE the
 * datum (a headline figure), not for a category's share.
 *
 * The count sits beside every bar in text, so the bar length is a redundant
 * cue rather than the only one (PRD §9.7).
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 10.1, PRD F15, §4.2, §9.7
 */
import { Empty } from '../../components/ui/Empty';

export interface MixRow {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  /** Screen-reader caption for the value, e.g. "3 uses". */
  readonly caption: string;
}

export interface MixListProps {
  readonly rows: readonly MixRow[];
  readonly emptyTitle: string;
  readonly emptyBody: string;
}

export function MixList({ rows, emptyTitle, emptyBody }: MixListProps): React.JSX.Element {
  if (rows.length === 0) {
    return <Empty title={emptyTitle}>{emptyBody}</Empty>;
  }

  const max = rows.reduce((m, row) => Math.max(m, row.value), 0);

  return (
    <ul className="flex flex-col gap-2.5">
      {rows.map((row) => {
        /* Guarded against an all-zero set, where max is 0 and the ratio would
           be NaN. A distribution can legitimately be all zeros in theory; the
           bar simply shows nothing. */
        const pct = max > 0 ? (row.value / max) * 100 : 0;

        return (
          <li key={row.key} className="flex items-center gap-3">
            <span className="text-body w-[150px] flex-none truncate text-xs font-light">
              {row.label}
            </span>
            <span className="bg-off-white relative h-4 flex-1 overflow-hidden rounded-full">
              <span
                className="bg-navy-text/70 absolute inset-y-0 left-0 rounded-full"
                style={{ width: `${String(pct)}%` }}
                aria-hidden="true"
              />
            </span>
            <span className="font-display text-navy-text w-6 flex-none text-right text-[13px] font-extrabold tabular-nums">
              {row.value}
              <span className="sr-only"> {row.caption}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
